import {
  Box,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import type { GeneratedTestCaseField } from '../types';

interface TestCaseInstanceFieldsProps {
  fields: GeneratedTestCaseField[];
}

const useStyles = makeStyles(theme => ({
  field: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
  },
  fieldLabel: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  pairedTableContainer: {
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  pairedTable: {
    width: '100%',
    tableLayout: 'fixed',
  },
  pairedHeaderCell: {
    width: '50%',
    fontWeight: 700,
    textAlign: 'center',
    backgroundColor: theme.palette.background.default,
    borderRight: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderRight: 0,
    },
  },
  pairedValueCell: {
    width: '50%',
    verticalAlign: 'top',
    whiteSpace: 'normal',
    borderRight: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderRight: 0,
    },
  },
}));

function normalizeFieldKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function normalizeItems(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) {
    return value;
  }

  return value ? [value] : [];
}

function renderValue(value: string | string[]) {
  if (Array.isArray(value)) {
    return value.map((item, index) => (
      <Typography key={`${item}-${index}`} variant="body2">
        • {item}
      </Typography>
    ));
  }

  return <Typography variant="body2">{value || '—'}</Typography>;
}

export const TestCaseInstanceFields = ({
  fields,
}: TestCaseInstanceFieldsProps) => {
  const classes = useStyles();

  const stepsField = fields.find(field => {
    const key = normalizeFieldKey(field.key);
    return key === 'steps' || key === 'teststeps';
  });

  const expectedResultsField = fields.find(field => {
    const key = normalizeFieldKey(field.key);
    return key === 'expectedresult' || key === 'expectedresults';
  });

  const remainingFields = fields.filter(field => {
    const key = normalizeFieldKey(field.key);

    return (
      key !== 'steps' &&
      key !== 'teststeps' &&
      key !== 'expectedresult' &&
      key !== 'expectedresults'
    );
  });

  const idFields = remainingFields.filter(field => {
    const key = normalizeFieldKey(field.key);
    return key === 'id' || key === 'testcaseid';
  });

  const nonIdFields = remainingFields.filter(field => {
    const key = normalizeFieldKey(field.key);
    return key !== 'id' && key !== 'testcaseid';
  });

  const orderedFields = [...idFields, ...nonIdFields];
  const preconditionsIndex = orderedFields.findIndex(
    field => normalizeFieldKey(field.key) === 'preconditions',
  );

  const fieldsBeforeSteps =
    preconditionsIndex >= 0
      ? orderedFields.slice(0, preconditionsIndex + 1)
      : orderedFields;

  const fieldsAfterSteps =
    preconditionsIndex >= 0 ? orderedFields.slice(preconditionsIndex + 1) : [];

  const steps = normalizeItems(stepsField?.value);
  const expectedResults = normalizeItems(expectedResultsField?.value);
  const rowCount = Math.max(steps.length, expectedResults.length);

  return (
    <>
      {fieldsBeforeSteps.map(field => (
        <Paper key={field.key} variant="outlined" className={classes.field}>
          <Typography variant="body2" className={classes.fieldLabel}>
            {field.label}
          </Typography>
          {renderValue(field.value)}
        </Paper>
      ))}

      {(stepsField || expectedResultsField) && (
        <Box className={classes.pairedTableContainer}>
          <Table size="small" className={classes.pairedTable}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.pairedHeaderCell}>
                  {stepsField?.label ?? 'Test Steps'}
                </TableCell>
                <TableCell className={classes.pairedHeaderCell}>
                  {expectedResultsField?.label ?? 'Expected Results'}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.from({ length: rowCount }, (_, index) => (
                <TableRow key={index}>
                  <TableCell className={classes.pairedValueCell}>
                    <Typography variant="body2">
                      {steps[index] || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell className={classes.pairedValueCell}>
                    <Typography variant="body2">
                      {expectedResults[index] || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {fieldsAfterSteps.map(field => (
        <Paper key={field.key} variant="outlined" className={classes.field}>
          <Typography variant="body2" className={classes.fieldLabel}>
            {field.label}
          </Typography>
          {renderValue(field.value)}
        </Paper>
      ))}
    </>
  );
};

export default TestCaseInstanceFields;
